import { useEffect, useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type Currency = components["schemas"]["CurrencyResponse"];

export function AvailableCurrenciesTable() {
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchCurrencies() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/v1/currencies/available");
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load currencies.");
      } else {
        setCurrencies(data ?? []);
      }
      setLoading(false);
    }
    fetchCurrencies();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        {loading && <Spinner className="size-5" />}
        {!loading && (
          <span className="text-sm text-muted-foreground">
            {currencies.length}{" "}
            {currencies.length === 1 ? "currency" : "currencies"}
          </span>
        )}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Symbol</TableHead>
              <TableHead className="text-right">Decimal Places</TableHead>
              <TableHead>ID</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currencies.length === 0 && !loading ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-muted-foreground h-24 text-center"
                >
                  No currencies found.
                </TableCell>
              </TableRow>
            ) : (
              currencies.map((currency) => (
                <TableRow key={currency.id ?? currency.code}>
                  <TableCell>
                    <Badge variant="secondary" className="font-mono text-xs">
                      {currency.code}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium">{currency.name}</TableCell>
                  <TableCell className="font-mono">{currency.symbol}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {currency.decimalPlaces ?? (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {currency.id ? (
                      <Badge variant="outline" className="font-mono text-xs">
                        {currency.id}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
